import { useState } from 'react'
import type { ActivityCatalogEntry } from '../types'
import { useDecisionStore } from '../store/decision'
import { CATEGORY_STYLES } from '../lib/categoryColors'
import { ACTIVITY_ICONS } from '../lib/activityIcons'

type Category = ActivityCatalogEntry['category']

const CATEGORY_LABEL: Record<Category, string> = {
  People: 'People & Skills',
  ChangeManagement: 'Change Management',
  Technology: 'Technology & Capability',
  Innovation: 'Innovation & Deployment',
  Governance: 'Governance & Risk',
}

const CATEGORY_ORDER: Category[] = ['Technology', 'Innovation', 'People', 'ChangeManagement', 'Governance']

export default function ActivityDeck({
  catalog, oneshotCounters,
}: {
  catalog: ActivityCatalogEntry[]
  oneshotCounters?: Record<string, number>
}) {
  const [filter, setFilter] = useState<Category | null>(null)

  const grouped = CATEGORY_ORDER.map((cat) => ({
    cat,
    items: catalog
      .filter((c) => c.category === cat)
      .filter((c) => !filter || filter === cat)
      .sort((a, b) => {
        const aLocked = !a.active && !a.unlocked ? 1 : 0
        const bLocked = !b.active && !b.unlocked ? 1 : 0
        return aLocked - bLocked
      }),
  })).filter((g) => g.items.length > 0)

  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-2">Activity Catalog</h2>
      <div className="flex gap-1 flex-wrap mb-4">
        <button
          className={`text-xs px-2 py-1 rounded border ${!filter
            ? 'bg-brand-600 text-white border-brand-600'
            : 'bg-slate-100 text-slate-700 border-transparent'}`}
          onClick={() => setFilter(null)}
        >
          All
        </button>
        {CATEGORY_ORDER.map((cat) => {
          const style = CATEGORY_STYLES[cat]
          const active = filter === cat
          return (
            <button
              key={cat}
              className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border ${
                active ? style.activeTab : 'bg-slate-100 text-slate-700 border-transparent'
              }`}
              onClick={() => setFilter(cat)}
            >
              <span aria-hidden className={`inline-block w-2 h-2 rounded-full ${style.dot}`} />
              {CATEGORY_LABEL[cat].split(' ')[0]}
            </button>
          )
        })}
      </div>

      <div className="space-y-6">
        {grouped.map((g) => (
          <section key={g.cat}>
            <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-2">
              {CATEGORY_LABEL[g.cat]}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {g.items.map((a) => (
                <ActivityCard
                  key={a.id}
                  card={a}
                  consecutiveFirings={oneshotCounters?.[a.id]}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

function ActivityCard({
  card, consecutiveFirings,
}: {
  card: ActivityCatalogEntry
  consecutiveFirings?: number
}) {
  const toggleStart = useDecisionStore((s) => s.toggleStart)
  const toggleStop = useDecisionStore((s) => s.toggleStop)
  const toggleOneShot = useDecisionStore((s) => s.toggleOneShot)
  const draft = useDecisionStore((s) => s.draft)

  const inStart = draft.programs_to_start.includes(card.id)
  const inStop = draft.programs_to_stop.includes(card.id)
  const inOneShot = draft.one_shots.includes(card.id)

  let state: 'active' | 'available' | 'locked'
  if (card.active) state = 'active'
  else if (card.unlocked) state = 'available'
  else state = 'locked'

  const categoryStyle = CATEGORY_STYLES[card.category]
  const stripeClass = state === 'locked' ? categoryStyle.stripeLocked : categoryStyle.stripe

  const stateClasses =
    state === 'locked'
      ? 'border-slate-200 bg-slate-100 opacity-70'
      : state === 'active'
      ? `${categoryStyle.activeCard}`
      : 'border-slate-200 bg-white hover:border-brand-400'

  const isOneShot = card.kind === 'one_shot'
  const qtrAmount = (card.monthly_cost_k * 3).toLocaleString('en-US')
  const startupAmount = card.one_shot_cost_k.toLocaleString('en-US')
  const costNode = isOneShot ? (
    <>
      <span className="font-semibold text-slate-800">${card.one_shot_cost_k}K</span>
      <span className="text-slate-500"> one-shot</span>
    </>
  ) : card.one_shot_cost_k > 0 ? (
    <>
      <span className="font-semibold text-slate-800">${qtrAmount}K</span>
      <span className="text-slate-500">/qtr · +</span>
      <span className="font-semibold text-slate-800">${startupAmount}K</span>
      <span className="text-slate-500"> start</span>
    </>
  ) : (
    <>
      <span className="font-semibold text-slate-800">${qtrAmount}K</span>
      <span className="text-slate-500">/qtr</span>
    </>
  )

  const onClick = () => {
    if (state === 'locked') return
    if (isOneShot) {
      toggleOneShot(card.id)
    } else if (card.active) {
      toggleStop(card.id)
    } else {
      toggleStart(card.id)
    }
  }

  const selectionLabel =
    inStart ? 'Will start this quarter' :
    inStop ? 'Will stop this quarter (wind-down cost applies)' :
    inOneShot ? 'Will run this quarter' : null

  return (
    <button
      onClick={onClick}
      disabled={state === 'locked'}
      className={`text-left rounded-md border border-l-4 ${stripeClass} px-3 py-2 transition-all ${stateClasses} ${
        (inStart || inOneShot) ? 'ring-2 ring-brand-500' : ''
      } ${inStop ? 'ring-2 ring-danger' : ''}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[11px] leading-none ${categoryStyle.chip}`}
          >
            {(() => {
              const Icon = ACTIVITY_ICONS[card.id]
              return Icon ? <Icon aria-hidden size={12} strokeWidth={2.25} /> : null
            })()}
            {card.id}
          </span>
          {state === 'active' && <span className="text-xs text-brand-700 font-medium">ACTIVE · {card.quarters_active}q</span>}
        </div>
        <span className="text-xs whitespace-nowrap tabular-nums">{costNode}</span>
      </div>
      <h4 className="font-semibold text-sm text-slate-900 mt-1">{card.name}</h4>
      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{card.description}</p>

      {state === 'locked' && card.locked_reasons[0] && (
        <div className="mt-1 text-xs text-slate-500 italic">
          🔒 {card.locked_reasons[0]}
        </div>
      )}
      {isOneShot && consecutiveFirings !== undefined && consecutiveFirings > 0 && (
        <div className="mt-1 text-xs text-amber-700">
          ⟲ Last used 1q ago
        </div>
      )}
      {selectionLabel && (
        <div className="mt-1 text-xs font-medium text-brand-700">
          {selectionLabel}
        </div>
      )}
    </button>
  )
}
